import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { noApp, ponte } from '../helpers/appNativo';

const VAPID_PUBLIC = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

// ---------------------------------------------------------------------------
// Dentro do app do Fio o caminho é OUTRO.
//
// WebView do Android não tem `PushManager`: o service worker registra, mas push
// nunca chega. Não é "não suportado" — é suportado por outra via. O aviso vem
// do token da Expo, que a casca do app entrega pela ponte.
//
// A divisão de trabalho é de propósito: o nativo só sabe pegar o TOKEN; quem
// fala com a API continua sendo esta tela, que já tem o axios com o JWT. Assim
// inscrever e desinscrever ficam num lugar só, com as mesmas regras dos dois
// lados — inclusive a de soltar a inscrição ANTES de limpar a sessão.
const MOTIVO_APP = {
  expo_go: 'Aviso com o app fechado não funciona no Expo Go, só no Fio instalado.',
  sem_projeto: 'O app não está ligado ao serviço de avisos. Chame a TI.',
  sem_aparelho: 'Aviso só funciona em celular de verdade, não no emulador.',
  negado: 'Você bloqueou os avisos. Libere nos ajustes do celular — o app não consegue pedir de novo.',
  sem_modulo: 'Esta versão do app não tem o aviso instalado. Chame a TI.',
  sem_permissao: 'Os avisos ainda não foram liberados neste aparelho.',
  erro: 'Não deu pra ativar o aviso. Chame a TI.',
};

async function tokenDoApp({ pedirPermissao }) {
  const app = ponte();
  if (!app) return { ok: false, motivo: 'erro' };
  try {
    return await (pedirPermissao ? app.ativarPush() : app.tokenPush());
  } catch (e) {
    return { ok: false, motivo: 'erro' };
  }
}

// O token da Expo ocupa a MESMA coluna do endpoint do Web Push, que é UNIQUE, e
// a gravação é upsert: aparelho compartilhado (ou troca de conta no mesmo
// celular) REASSOCIA a linha. Sem isso, quem saiu continuaria recebendo no
// celular dele os clientes de quem entrou depois.
async function inscreverTokenDoApp(token, plataforma) {
  await axios.post(`${API}/notificacoes/subscrever`, { endpoint: token, platform: plataforma || 'android' });
}

export function pushSuportado() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function detectarPlataforma() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isStandalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  return { isIOS, isStandalone };
}

export async function registrarServiceWorker() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js');
  } catch (e) {
    return null;
  }
}

// Estado atual: { suportado, permissao, inscrito, iosPrecisaInstalar }
export async function estadoPush() {
  if (noApp()) {
    const r = await tokenDoApp({ pedirPermissao: false });
    return {
      suportado: true,
      // 'default' e nao 'denied' quando so falta pedir: a diferenca decide se a
      // tela oferece o botao ou manda a pessoa nos ajustes do celular.
      permissao: r.ok ? 'granted' : (r.motivo === 'negado' ? 'denied' : 'default'),
      inscrito: !!r.ok,
      iosPrecisaInstalar: false,
    };
  }
  const suportado = pushSuportado();
  const { isIOS, isStandalone } = detectarPlataforma();
  if (!suportado) {
    return { suportado: false, permissao: 'unsupported', inscrito: false, iosPrecisaInstalar: isIOS && !isStandalone };
  }
  const permissao = Notification.permission;
  let inscrito = false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    inscrito = !!sub;
  } catch (e) { /* ignora */ }
  return { suportado: true, permissao, inscrito, iosPrecisaInstalar: isIOS && !isStandalone };
}

// Pede permissao, assina no PushManager e salva no backend (upsert por endpoint).
export async function ativarPush() {
  if (noApp()) {
    const r = await tokenDoApp({ pedirPermissao: true });
    if (!r.ok) throw new Error(MOTIVO_APP[r.motivo] || MOTIVO_APP.erro);
    await inscreverTokenDoApp(r.token, r.plataforma);
    return { endpoint: r.token };
  }
  if (!pushSuportado()) throw new Error('Notificações não suportadas neste navegador.');
  if (!VAPID_PUBLIC) throw new Error('Push não configurado (VAPID ausente).');
  const permissao = await Notification.requestPermission();
  if (permissao !== 'granted') throw new Error('Permissão negada.');

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC),
    });
  }
  const { endpoint, keys } = sub.toJSON();
  const { isIOS, isStandalone } = detectarPlataforma();
  const platform = isIOS ? 'ios' : (/Android/.test(navigator.userAgent) ? 'android' : 'desktop');
  await axios.post(`${API}/notificacoes/subscrever`, { endpoint, p256dh: keys.p256dh, auth: keys.auth, platform });
  return { endpoint };
}

export async function desativarPush() {
  if (noApp()) {
    // Sem pedir permissão: quem está DESLIGANDO o aviso não pode ser
    // interrompido por um pedido de permissão.
    const r = await tokenDoApp({ pedirPermissao: false });
    if (r.ok) {
      try { await axios.post(`${API}/notificacoes/desinscrever`, { endpoint: r.token }); } catch (e) { /* ignora */ }
    }
    return;
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const { endpoint } = sub.toJSON();
      await sub.unsubscribe();
      await axios.post(`${API}/notificacoes/desinscrever`, { endpoint });
    }
  } catch (e) { /* ignora */ }
}

// ---------------------------------------------------------------------------
// Amarrar o aviso à PESSOA que está logada agora.
//
// Um aparelho, várias pessoas — é a regra na loja, não a exceção. O que
// identifica a inscrição é o aparelho (endpoint do navegador ou token da Expo),
// e o que decide PRA QUEM mandar é a linha no servidor. Se ninguém refizer esse
// laço, quem sai continua recebendo no celular os clientes de quem entrou
// depois.
//
// Por isso o par: soltar ao sair, reamarrar ao entrar. Nenhum dos dois pergunta
// nada — quando a permissão já foi dada uma vez, refazer a inscrição é
// silencioso, e pedir de novo a cada login seria pedir pelo que já foi
// respondido.

/** Reamarra a inscrição deste aparelho ao usuário logado. Silencioso. */
export async function sincronizarPush() {
  try {
    if (noApp()) {
      const r = await tokenDoApp({ pedirPermissao: false });
      if (r.ok) await inscreverTokenDoApp(r.token, r.plataforma);
      return;
    }
    if (!pushSuportado() || Notification.permission !== 'granted' || !VAPID_PUBLIC) return;
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC),
      });
    }
    const { endpoint, keys } = sub.toJSON();
    const { isIOS } = detectarPlataforma();
    const platform = isIOS ? 'ios' : (/Android/.test(navigator.userAgent) ? 'android' : 'desktop');
    await axios.post(`${API}/notificacoes/subscrever`, { endpoint, p256dh: keys.p256dh, auth: keys.auth, platform });
  } catch (e) { /* aviso é acessório: nunca pode atrapalhar entrar no Fio */ }
}

/**
 * Solta a inscrição deste aparelho no SERVIDOR, ao sair.
 *
 * De propósito NÃO cancela a inscrição do navegador nem a permissão do
 * celular: a pessoa autorizou o aviso neste aparelho uma vez, e o próximo login
 * reamarra sozinho pelo `sincronizarPush`. Cancelar de verdade obrigaria a
 * clicar em "ativar avisos" a cada volta — e quem esquecesse ficaria sem saber
 * que tem cliente esperando.
 *
 * Tem que rodar ANTES de limpar o token: depois não há como se autenticar.
 */
export async function soltarPush() {
  try {
    if (noApp()) {
      const r = await tokenDoApp({ pedirPermissao: false });
      if (r.ok) await axios.post(`${API}/notificacoes/desinscrever`, { endpoint: r.token });
      return;
    }
    if (!pushSuportado()) return;
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) await axios.post(`${API}/notificacoes/desinscrever`, { endpoint: sub.toJSON().endpoint });
  } catch (e) { /* sair não pode falhar por causa do aviso */ }
}


// --- Notificação que some quando a conversa é lida -------------------------
//
// Pedido do Gabriel (10/09/2026): a notificação ficava na bandeja do Android
// mesmo depois de ele abrir a conversa pelo Fio — só saía quando ele apagava na
// mão. O WhatsApp apaga sozinho quando você lê, e é esse o comportamento que a
// pessoa espera.
//
// O servidor marca cada push com a tag `conversa-<id>` (notificacaoService), e
// o service worker usa essa tag pra empilhar as mensagens da mesma conversa.
// Aqui a tela usa a mesma tag pra fechar.
//
// `getRegistration()` e não `.ready`: `.ready` nunca resolve num navegador sem
// service worker registrado, e a tela ficaria esperando pra sempre.
//
// No APP nativo quem mostra a notificação é o Android (Expo), não o service
// worker — isto não alcança lá. Fica pra quando o app ganhar o método na ponte.

async function notificacoesAbertas() {
  if (noApp() || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return [];
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg?.getNotifications) return [];
  return reg.getNotifications();
}

/** Fecha as notificações destas conversas. Silencioso: é conveniência. */
export async function fecharNotificacoesDeConversas(ids) {
  try {
    const alvo = new Set((ids || []).filter(Boolean).map((id) => `conversa-${id}`));
    if (!alvo.size) return;
    (await notificacoesAbertas()).forEach((n) => { if (alvo.has(n.tag)) n.close(); });
  } catch (e) { /* notificação que não fecha não pode quebrar a conversa */ }
}
